// ============================================================================
// LOGIN ALUNO
// ============================================================================
// Arquivo: src/pages/LoginAluno.tsx
// UPDATED: 2026-03-26
// ✅ Conectado à API real via authService
// ✅ Login por ID (ALU-2026-XXXX) ou e-mail
// ✅ studentId e affiliateId populados no Redux após login
// ============================================================================

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, LogIn, AlertCircle } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { login } from '../store/slices/authSlice';
import { authService } from '../services/authService';

const LoginAluno: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData]         = useState({ login: '', senha: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // ✅ Chama a API real — aceita ALU-2026-XXXX ou email
      const response = await authService.login({
        email:    formData.login.trim(),
        password: formData.senha,
        userType: 'student',
      });

      const user = authService.getCachedUser();
      if (!user) throw new Error('Erro ao carregar dados do usuário');

      // ✅ Redux recebe studentId e affiliateId (escola que cadastrou)
      dispatch(login({
        user: {
          id:          user.id,
          name:        user.name,
          email:       user.email,
          role:        'student',
          userType:    'student',
          systemId:    user.systemId,
          studentId:   user.studentId,    // ← para acessar dados do aluno
          affiliateId: user.affiliateId,  // ← escola que o cadastrou
          status:      user.status,
          photoUrl:    user.photoUrl,
        },
        token: response.accessToken,
      }));

      navigate('/portal/aluno');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? '';
      if (msg.toLowerCase().includes('credential') || msg.toLowerCase().includes('invalid')) {
        setError('ID ou senha incorretos. Tente novamente.');
      } else if (msg.toLowerCase().includes('inactive') || msg.toLowerCase().includes('suspended')) {
        setError('Sua conta está inativa. Entre em contato com sua escola.');
      } else if (msg) {
        setError(msg);
      } else {
        setError('Erro ao conectar. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

        .alu-root * { box-sizing: border-box; margin: 0; padding: 0; }
        .alu-root {
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh; background: #060a12;
          display: flex; align-items: center; justify-content: center;
          position: relative; overflow: hidden; padding: 24px;
        }
        .alu-bg { position: absolute; inset: 0; z-index: 0; }
        .alu-bg::before {
          content: ''; position: absolute;
          width: 600px; height: 600px; top: -200px; left: -200px;
          background: radial-gradient(circle, rgba(30,100,240,0.16) 0%, transparent 70%);
          animation: alu-drift1 13s ease-in-out infinite alternate;
        }
        .alu-bg::after {
          content: ''; position: absolute;
          width: 500px; height: 500px; bottom: -150px; right: -150px;
          background: radial-gradient(circle, rgba(20,60,180,0.12) 0%, transparent 70%);
          animation: alu-drift2 16s ease-in-out infinite alternate;
        }
        @keyframes alu-drift1 { from { transform: translate(0,0); } to { transform: translate(65px,85px); } }
        @keyframes alu-drift2 { from { transform: translate(0,0); } to { transform: translate(-55px,-65px); } }
        .alu-grid {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(60,120,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(60,120,255,0.04) 1px, transparent 1px);
          background-size: 48px 48px;
        }
        .alu-card {
          position: relative; z-index: 1;
          width: 100%; max-width: 460px;
          animation: alu-fadeUp 0.6s cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes alu-fadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .alu-back-link {
          display: inline-flex; align-items: center; gap: 8px;
          color: rgba(255,255,255,0.5); font-size: 13px; font-weight: 500;
          text-decoration: none; margin-bottom: 24px; transition: color 0.2s;
        }
        .alu-back-link:hover { color: #5b9aff; }
        .alu-logo-wrap {
          text-align: center; margin-bottom: 28px;
          animation: alu-fadeUp 0.6s 0.05s cubic-bezier(0.16,1,0.3,1) both;
        }
        .alu-logo-ring {
          display: inline-flex; align-items: center; justify-content: center;
          width: 96px; height: 96px; border-radius: 50%;
          background: linear-gradient(135deg, #0c1830 0%, #060e20 100%);
          border: 2px solid rgba(60,120,255,0.3);
          box-shadow: 0 0 0 8px rgba(30,80,220,0.08), 0 20px 60px rgba(0,0,0,0.5);
          margin: 0 auto 18px; overflow: hidden; transition: box-shadow 0.3s;
        }
        .alu-logo-ring:hover { box-shadow: 0 0 0 12px rgba(30,80,220,0.13), 0 24px 70px rgba(0,0,0,0.6); }
        .alu-logo-ring img {
          width: 64px; height: 64px; object-fit: contain;
          filter: drop-shadow(0 2px 8px rgba(80,150,255,0.4));
        }
        .alu-title {
          font-family: 'Playfair Display', serif;
          font-size: 28px; font-weight: 900;
          color: #fff; letter-spacing: -0.5px; line-height: 1.1;
        }
        .alu-subtitle { color: #3a5080; font-size: 13px; font-weight: 400; margin-top: 6px; }
        .alu-panel {
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(60,120,255,0.12);
          border-radius: 20px; padding: 36px;
          backdrop-filter: blur(20px);
          box-shadow: 0 32px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(60,120,255,0.07);
          animation: alu-fadeUp 0.6s 0.1s cubic-bezier(0.16,1,0.3,1) both;
        }
        .alu-panel-header {
          display: flex; align-items: center; gap: 10px;
          margin-bottom: 28px; padding-bottom: 20px;
          border-bottom: 1px solid rgba(60,120,255,0.1);
        }
        .alu-badge {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(30,80,220,0.18); border: 1px solid rgba(60,120,255,0.28);
          border-radius: 30px; padding: 5px 12px;
          font-size: 11px; font-weight: 600;
          color: #5b9aff; letter-spacing: 0.8px; text-transform: uppercase;
        }
        .alu-badge-dot {
          width: 6px; height: 6px; border-radius: 50%; background: #5b9aff;
          box-shadow: 0 0 6px rgba(91,154,255,0.8);
          animation: alu-pulse 2s ease-in-out infinite;
        }
        @keyframes alu-pulse {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:0.6; transform:scale(0.85); }
        }
        .alu-panel-title {
          font-family: 'Playfair Display', serif;
          font-size: 20px; font-weight: 700; color: #c8d8f0; margin-left: auto;
        }
        .alu-error {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 12px 16px;
          background: rgba(220,40,40,0.1); border: 1px solid rgba(220,40,40,0.25);
          border-radius: 12px; margin-bottom: 22px;
          animation: alu-shake 0.35s ease;
        }
        @keyframes alu-shake {
          0%,100% { transform:translateX(0); } 20% { transform:translateX(-6px); }
          40% { transform:translateX(6px); }   60% { transform:translateX(-4px); }
          80% { transform:translateX(4px); }
        }
        .alu-error svg { color: #f87171; flex-shrink:0; margin-top:1px; }
        .alu-error p   { color: #fca5a5; font-size:13px; font-weight:500; line-height:1.4; }
        .alu-field { margin-bottom: 18px; }
        .alu-label {
          display: block; font-size: 11.5px; font-weight: 600;
          letter-spacing: 0.7px; text-transform: uppercase;
          color: #3a5080; margin-bottom: 8px;
        }
        .alu-hint { color: #22304a; font-size: 11px; margin-top: 6px; }
        .alu-input-wrap { position: relative; }
        .alu-input {
          width: 100%; padding: 13px 16px;
          background: rgba(255,255,255,0.04);
          border: 1.5px solid rgba(60,120,255,0.15);
          border-radius: 12px; color: #c8d8f0;
          font-family: 'DM Sans', sans-serif; font-size: 14.5px; font-weight: 500;
          outline: none; transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
          caret-color: #5b9aff;
        }
        .alu-input::placeholder { color: #1e2e48; }
        .alu-input:focus {
          border-color: rgba(91,154,255,0.6);
          background: rgba(20,60,160,0.1);
          box-shadow: 0 0 0 3px rgba(91,154,255,0.1);
        }
        .alu-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .alu-input.with-btn { padding-right: 46px; }
        .alu-eye {
          position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: #2a3a58; display: flex; align-items: center; justify-content: center;
          padding: 0; transition: color 0.2s;
        }
        .alu-eye:hover { color: #3a5080; }
        .alu-row {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 22px; margin-top: -4px;
        }
        .alu-check-label {
          display: flex; align-items: center; gap: 8px; cursor: pointer;
          color: rgba(255,255,255,0.45); font-size: 13px;
        }
        .alu-check-label input[type="checkbox"] { width:15px; height:15px; accent-color:#5b9aff; }
        .alu-forgot { color:#5b9aff; font-size:13px; font-weight:600; text-decoration:none; transition:color 0.2s; }
        .alu-forgot:hover { color: #8dc0ff; }
        .alu-btn {
          width: 100%; padding: 14px; border: none; border-radius: 12px;
          background: linear-gradient(135deg, #1a4fc0 0%, #0e2e80 100%);
          color: #fff; font-family: 'DM Sans', sans-serif;
          font-size: 15px; font-weight: 700; letter-spacing: 0.3px;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          gap: 8px; box-shadow: 0 8px 32px rgba(20,70,200,0.4);
          transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s;
          position: relative; overflow: hidden;
        }
        .alu-btn::before {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.13) 0%, transparent 60%);
          border-radius: inherit;
        }
        .alu-btn:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 14px 40px rgba(20,70,200,0.5); }
        .alu-btn:active:not(:disabled) { transform: translateY(0); }
        .alu-btn:disabled { opacity:0.5; cursor:not-allowed; transform:none; }
        .alu-divider { display:flex; align-items:center; gap:12px; margin:26px 0; }
        .alu-divider-line { flex:1; border:none; border-top:1px solid rgba(60,120,255,0.1); }
        .alu-divider-text { color:#22304a; font-size:12px; white-space:nowrap; }
        .alu-cta { text-align: center; }
        .alu-cta p { color:rgba(255,255,255,0.4); font-size:13px; margin-bottom:14px; }
        .alu-cta-btn {
          display: inline-block;
          background: rgba(91,154,255,0.1); border: 1.5px solid rgba(91,154,255,0.3);
          color: #5b9aff; font-weight:700; font-size:14px;
          padding: 11px 28px; border-radius: 10px;
          text-decoration: none; transition: all 0.2s;
        }
        .alu-cta-btn:hover { background:rgba(91,154,255,0.18); border-color:rgba(91,154,255,0.55); transform:translateY(-1px); }
        .alu-footer {
          text-align: center; margin-top: 20px;
          animation: alu-fadeUp 0.6s 0.2s cubic-bezier(0.16,1,0.3,1) both;
        }
        .alu-footer p { color:#1e2e48; font-size:12px; margin-bottom:6px; }
        .alu-footer a { color:#4ade80; font-size:13px; font-weight:600; text-decoration:none; transition:color 0.2s; }
        .alu-footer a:hover { color: #86efac; }
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
      `}</style>

      <div className="alu-root">
        <div className="alu-bg"><div className="alu-grid" /></div>

        <div className="alu-card">

          <Link to="/" className="alu-back-link">
            <ArrowLeft size={16} /> Voltar para Home
          </Link>

          <div className="alu-logo-wrap">
            <div className="alu-logo-ring">
              <img src="/imagem/Bras.png" alt="Federação dos Profissionais da Beleza" />
            </div>
            <h1 className="alu-title">Portal do Aluno</h1>
            <p className="alu-subtitle">Acesse seus certificados e cursos</p>
          </div>

          <div className="alu-panel">
            <div className="alu-panel-header">
              <span className="alu-badge">
                <span className="alu-badge-dot" /> Área Restrita
              </span>
              <span className="alu-panel-title">Alunos</span>
            </div>

            {error && (
              <div className="alu-error">
                <AlertCircle size={16} />
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>

              {/* ID ou Email */}
              <div className="alu-field">
                <label className="alu-label">ID do Aluno ou E-mail</label>
                <div className="alu-input-wrap">
                  <input
                    type="text"
                    value={formData.login}
                    onChange={e => { setFormData({ ...formData, login: e.target.value }); setError(''); }}
                    placeholder="ALU-2026-XXXXXX ou email@exemplo.com"
                    disabled={loading}
                    className="alu-input"
                    required
                  />
                </div>
                <p className="alu-hint">Use seu ID (ex: ALU-2026-XXXX) ou e-mail cadastrado</p>
              </div>

              {/* Senha */}
              <div className="alu-field">
                <label className="alu-label">Senha</label>
                <div className="alu-input-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.senha}
                    onChange={e => { setFormData({ ...formData, senha: e.target.value }); setError(''); }}
                    placeholder="••••••••"
                    disabled={loading}
                    className="alu-input with-btn"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(s => !s)} className="alu-eye" tabIndex={-1}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="alu-hint">Senha padrão fornecida pela sua escola: alu@feder26</p>
              </div>

              {/* Remember / Forgot */}
              <div className="alu-row">
                <label className="alu-check-label">
                  <input type="checkbox" /> Lembrar-me
                </label>
                <Link to="/recuperar-senha" className="alu-forgot">Esqueci minha senha</Link>
              </div>

              <button
                type="submit"
                disabled={loading || !formData.login || !formData.senha}
                className="alu-btn"
              >
                {loading
                  ? <><div style={{ width:18, height:18, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 1s linear infinite' }} /> Entrando...</>
                  : <><LogIn size={18} /> Acessar Portal</>}
              </button>
            </form>

            <div className="alu-divider">
              <hr className="alu-divider-line" />
              <span className="alu-divider-text">Ainda não é aluno?</span>
              <hr className="alu-divider-line" />
            </div>

            <div className="alu-cta">
              <p>Faça parte da maior rede de profissionais da beleza</p>
              <Link to="/filiacao" className="alu-cta-btn">Cadastre-se Agora</Link>
            </div>
          </div>

          <div className="alu-footer">
            <p>Problemas para acessar? Entre em contato com sua escola</p>
            <a href="https://wa.me/5598970190739" target="_blank" rel="noopener noreferrer">
              WhatsApp: (98) 97019-0739
            </a>
          </div>

        </div>
      </div>
    </>
  );
};

export default LoginAluno;